import { useTranslation } from 'react-i18next';

/** First-load boot loader — v3's pulsating "A" (index.html #main-loading-spinner)
 *  carried into the React boot path, replacing the generic auth-gate skeleton.
 *  Inline SVG on purpose: the pre-init boot path must never wait on a lazy
 *  chunk or an asset fetch (same law as the static pre-init error screen).
 *  Paths and fills are teal_icon_logo.svg verbatim — the brand gradient tile,
 *  matching the pre-React index.html spinner in every theme (it's the app
 *  icon; the themed bg-background wrapper carries light/dark). Gradient/filter
 *  ids are prefixed boot-logo-* because inline SVG ids are document-global. */
export const BootLogoLoader = () => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t('CommonText.loading')}
      className="flex min-h-svh w-full items-center justify-center bg-background"
      data-testid="v4-boot-logo-loader"
    >
      <svg
        viewBox="0 0 81 81"
        fill="none"
        aria-hidden="true"
        className="size-14 motion-safe:animate-ap-logo-pulse"
        data-slot="boot-logo"
      >
        <path
          d="M23.6045 0.0185547H56.9648C69.9911 0.0185547 80.5508 10.5782 80.5508 23.6045V56.9648C80.5508 69.9911 69.9911 80.5508 56.9648 80.5508H23.6045C10.5782 80.5508 0.0185547 69.9911 0.0185547 56.9648V23.6045C0.0185547 10.5782 10.5782 0.0185547 23.6045 0.0185547Z"
          fill="url(#boot-logo-grad-tile)"
          stroke="white"
          strokeWidth="0.0361625"
        />
        <path
          d="M80.4749 53.091C80.5356 53.1517 80.5696 53.234 80.5696 53.3198V56.966C80.5696 70.0031 70.0057 80.5725 56.9753 80.5725H36.8025C36.7166 80.5725 36.6343 80.5384 36.5736 80.4777L18.9295 62.8245C18.832 62.7269 18.8071 62.5782 18.8674 62.4542L35.9935 27.2532C36.0092 27.2209 36.0302 27.1915 36.0556 27.1661L45.0814 18.1356C45.2078 18.0092 45.4127 18.0092 45.5391 18.1356L80.4749 53.091Z"
          fill="url(#boot-logo-grad-shade)"
        />
        <g filter="url(#boot-logo-glyph-shadow)">
          <path
            d="M47.6317 54.3023C47.6317 54.3016 47.6311 54.301 47.6303 54.301H33.0065C32.9119 54.301 32.8283 54.3626 32.8004 54.453L30.312 62.5081C30.284 62.5985 30.2005 62.6602 30.1059 62.6602H19.0501C18.9001 62.6602 18.7959 62.5109 18.8476 62.3702L35.1008 18.052C35.132 17.967 35.2129 17.9105 35.3034 17.9105H45.1446C45.2349 17.9105 45.3156 17.9668 45.3469 18.0515L61.7233 62.3712C61.7754 62.5121 61.6711 62.6617 61.521 62.6617H50.4025C50.3082 62.6617 50.2248 62.6004 50.1966 62.5104L47.6317 54.3028C47.6317 54.3026 47.6317 54.3025 47.6317 54.3023V54.3023ZM35.516 45.6923C35.4731 45.8311 35.5769 45.9717 35.7221 45.9717H44.7564C44.9018 45.9717 45.0055 45.8308 44.9624 45.6919L40.4312 31.1156C40.3683 30.9132 40.0817 30.9134 40.0191 31.116L35.516 45.6923Z"
            fill="white"
          />
        </g>
        <defs>
          <filter
            id="boot-logo-glyph-shadow"
            x="16.834"
            y="16.9105"
            width="46.9028"
            height="48.7512"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="1" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.271439 0 0 0 0 0.304199 0 0 0 0.07 0"
            />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
          <linearGradient
            id="boot-logo-grad-tile"
            x1="6.9129"
            y1="6.9129"
            x2="73.6563"
            y2="73.6563"
            gradientUnits="userSpaceOnUse"
          >
            {/* eslint-disable-next-line no-restricted-syntax -- brand mark gradient stop, teal_icon_logo.svg verbatim: theme-invariant app icon, not a themeable surface */}
            <stop stopColor="#60ECFE" />
            {/* eslint-disable-next-line no-restricted-syntax -- brand mark gradient stop, teal_icon_logo.svg verbatim: theme-invariant app icon, not a themeable surface */}
            <stop offset="1" stopColor="#036C7A" />
          </linearGradient>
          <linearGradient
            id="boot-logo-grad-shade"
            x1="27.4918"
            y1="35.7343"
            x2="69.5492"
            y2="77.7699"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopOpacity="0.5" />
            <stop offset="1" stopOpacity="0.5" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};
